import type { KnowledgeBaseTocHeading } from 'src/types/mindtime/knowledgeBase';

export const KB_IMAGE_BASE = 'https://kb.mindtime.eu';
export const KB_STORAGE_KEY_LAST_PAGE = 'kb_last_selected_page';
export const KB_STORAGE_KEY_EXPANDED_FOLDERS = 'kb_expanded_folders';
export const KB_SUPPORT_PATH = '/mindtime/support';
export const KB_ARTICLE_BASE_PATH = '/mindtime/support/knowledge-base';

export const normalizeKbArticlePath = (path?: string | null): string => {
  if (!path) return '';
  return path
    .replace(/^\/+|\/+$/g, '')
    .replace(/^external\//i, '')
    .replace(/^external$/i, '');
};

export const buildKbArticleHref = (articlePath?: string | null): string => {
  const clean = normalizeKbArticlePath(articlePath);
  return clean ? `${KB_ARTICLE_BASE_PATH}/${clean}` : KB_ARTICLE_BASE_PATH;
};

export const parseKbArticlePathFromPathname = (pathname: string): string | null => {
  if (!pathname.startsWith(KB_ARTICLE_BASE_PATH)) return null;
  if (pathname === KB_ARTICLE_BASE_PATH) return null;
  const rest = pathname.slice(KB_ARTICLE_BASE_PATH.length).replace(/^\/+/, '').replace(/\/+$/, '');
  return rest || null;
};

export const isKbArticleLocation = (pathname: string): boolean =>
  pathname === KB_ARTICLE_BASE_PATH || pathname.startsWith(`${KB_ARTICLE_BASE_PATH}/`);

export const readKbExpandedFolders = (): number[] => {
  try {
    const raw = localStorage.getItem(KB_STORAGE_KEY_EXPANDED_FOLDERS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === 'number');
  } catch {
    return [];
  }
};

export const writeKbExpandedFolders = (folderIds: number[]): void => {
  localStorage.setItem(KB_STORAGE_KEY_EXPANDED_FOLDERS, JSON.stringify(folderIds));
};

export const generateKbSlug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const stripHtmlTags = (value: string): string => value.replace(/<[^>]*>/g, '').trim();

const extractMarkdownHeadings = (markdown: string): KnowledgeBaseTocHeading[] => {
  const headingRegex = /^(#{1,4})\s+(.+)$/gm;
  const headings: KnowledgeBaseTocHeading[] = [];
  let match = headingRegex.exec(markdown);

  while (match) {
    const level = match[1].length;
    const text = match[2].trim();
    headings.push({ id: generateKbSlug(text), text, level });
    match = headingRegex.exec(markdown);
  }

  return headings;
};

const extractHtmlHeadings = (html: string): KnowledgeBaseTocHeading[] => {
  const headingRegex = /<h([1-4])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>/gi;
  const headings: KnowledgeBaseTocHeading[] = [];
  const usedIds = new Map<string, number>();
  let match = headingRegex.exec(html);

  while (match) {
    const level = Number(match[1]);
    const text = stripHtmlTags(match[2]);
    if (text) {
      const baseId = generateKbSlug(text) || `heading-${headings.length + 1}`;
      const seen = usedIds.get(baseId) ?? 0;
      usedIds.set(baseId, seen + 1);
      const id = seen === 0 ? baseId : `${baseId}-${seen + 1}`;
      headings.push({ id, text, level });
    }
    match = headingRegex.exec(html);
  }

  return headings;
};

export const hasKbHtmlHeadings = (content: string): boolean => /<h[1-4][\s>]/i.test(content);

export const isKbHtmlContent = (content: string): boolean => {
  const trimmed = content.trim();
  if (hasKbHtmlHeadings(trimmed)) return true;
  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) return true;
  // Treat as HTML when it starts with a block tag and has no markdown headings.
  if (
    /^<(?:div|p|section|article|table|ul|ol|header|body)\b/i.test(trimmed) &&
    !/^#{1,4}\s+.+$/m.test(trimmed)
  ) {
    return true;
  }
  return false;
};

export const extractKbHeadings = (content: string): KnowledgeBaseTocHeading[] => {
  if (hasKbHtmlHeadings(content)) {
    return extractHtmlHeadings(content);
  }
  return extractMarkdownHeadings(content);
};

/** Ensure h1–h4 elements have id attributes matching extractKbHeadings slugs. */
export const injectKbHeadingIds = (html: string): string => {
  const usedIds = new Map<string, number>();

  return html.replace(/<h([1-4])([^>]*)>([\s\S]*?)<\/h\1>/gi, (match, level, attrs, inner) => {
    if (/\sid\s*=/i.test(attrs)) return match;

    const text = stripHtmlTags(inner);
    const baseId = generateKbSlug(text) || 'heading';
    const seen = usedIds.get(baseId) ?? 0;
    usedIds.set(baseId, seen + 1);
    const id = seen === 0 ? baseId : `${baseId}-${seen + 1}`;

    return `<h${level}${attrs} id="${id}">${inner}</h${level}>`;
  });
};

/**
 * When markdown ATX headings are mixed into an HTML payload (e.g. with <img> tags),
 * convert those lines into real heading elements with ids so TOC clicks work.
 */
export const convertMarkdownHeadingsInHtml = (content: string): string =>
  content.replace(/^(#{1,4})\s+(.+)$/gm, (_match, hashes: string, text: string) => {
    const level = hashes.length;
    const clean = text.trim();
    const id = generateKbSlug(clean);
    return `<h${level} id="${id}">${clean}</h${level}>`;
  });


export const buildKbTocTree = (headings: KnowledgeBaseTocHeading[]): KnowledgeBaseTocHeading[] => {
  const result: KnowledgeBaseTocHeading[] = [];
  const stack: KnowledgeBaseTocHeading[] = [];

  headings.forEach((heading) => {
    const newHeading: KnowledgeBaseTocHeading = { ...heading, children: [] };

    while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      result.push(newHeading);
    } else {
      const parent = stack[stack.length - 1];
      if (!parent.children) parent.children = [];
      parent.children.push(newHeading);
    }

    stack.push(newHeading);
  });

  return result;
};

export const fixKbHtmlImageSrcs = (html: string): string =>
  html.replace(/(<img[^>]+src=")([^"]*?)"/gi, (match, prefix, src) => {
    const isBase64 = src.startsWith('data:');
    const hasBase =
      src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//');
    if (isBase64 || hasBase) return match;
    const normalizedSrc = src.startsWith('/') ? src : `/${src}`;
    return `${prefix}${KB_IMAGE_BASE}${normalizedSrc}" referrerpolicy="no-referrer"`;
  });

export const resolveKbImageSrc = (src?: string): string | undefined => {
  if (!src) return src;
  const isBase64 = src.startsWith('data:');
  const hasBase =
    src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//');
  if (isBase64 || hasBase) return src;
  const normalizedSrc = src.startsWith('/') ? src : `/${src}`;
  return `${KB_IMAGE_BASE}${normalizedSrc}`;
};

/** Recursively pull plain text from ReactMarkdown / React children. */
export const getKbNodeText = (node: unknown): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getKbNodeText).join('');
  if (typeof node === 'object' && node !== null && 'props' in node) {
    const { props } = node as { props?: { children?: unknown } };
    return getKbNodeText(props?.children);
  }
  return '';
};

/**
 * Stamp TOC heading ids onto rendered h1–h4 in document order so clicks
 * always resolve, regardless of markdown vs HTML rendering.
 */
export const applyKbHeadingIdsToDom = (
  container: HTMLElement,
  headings: Array<{ id: string }>
): void => {
  const elements = container.querySelectorAll('h1, h2, h3, h4');
  headings.forEach((heading, index) => {
    const element = elements[index] as HTMLElement | undefined;
    if (element && heading.id) {
      element.id = heading.id;
    }
  });
};
